/**
 * Favicon helpers for white-label / partner branding.
 *
 * Two concerns live here:
 *  - `applyFavicon` points EVERY `<link rel="icon">` variant (plus the
 *    `shortcut icon` and `apple-touch-icon`) at a single href. The static
 *    `index.html` ships 16px/32px/apple Tagada icons; updating only the first
 *    one (the previous behaviour) left stale Tagada icons on the other
 *    variants for partner brands. We replace them all.
 *  - `generateInitialsDataUrl` is the last-resort fallback: it paints the
 *    brand initials on the brand colour so a partner that ships no favicon
 *    asset (or a broken path) still gets a recognizable, on-brand tab icon
 *    instead of the default Tagada one.
 */

/** Selector covering every favicon-ish link the document may carry. */
const FAVICON_SELECTOR = 'link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]';

/** 1×1 transparent PNG — a blank favicon for incomplete (unnamed) partner brands. */
const BLANK_FAVICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

/**
 * Brand initials: 1–2 uppercase letters.
 * "Payoren" -> "P", "Test Partner" -> "TP", "Suby.fi" -> "SU".
 */
export function brandInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** WCAG-ish luminance check to pick readable foreground text on the brand colour. */
function isLightHex(hex: string): boolean {
  const clean = hex.replace(/^#/, '');
  if (clean.length < 6) return false;
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.5;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Generate a square PNG data URL with the brand initials on the brand colour.
 * Returns null when canvas isn't available (e.g. SSR / unsupported env).
 */
export function generateInitialsDataUrl(name: string, bgColor: string): string | null {
  if (typeof document === 'undefined') return null;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const bg = /^#?[0-9a-fA-F]{6}$/.test(bgColor) ? bgColor : '#64748b';

  ctx.fillStyle = bg;
  roundedRectPath(ctx, 0, 0, size, size, size * 0.22);
  ctx.fill();

  ctx.fillStyle = isLightHex(bg) ? '#1a1a1a' : '#ffffff';
  ctx.font = `600 ${Math.round(size * 0.5)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Nudge down slightly: optical centering for uppercase glyphs.
  ctx.fillText(brandInitials(name), size / 2, size / 2 + size * 0.04);

  return canvas.toDataURL('image/png');
}

/** Point every favicon link in <head> at `href`, creating one if none exist. */
export function applyFavicon(href: string): void {
  if (typeof document === 'undefined' || !href) return;

  const links = document.head.querySelectorAll<HTMLLinkElement>(FAVICON_SELECTOR);
  if (links.length === 0) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
    return;
  }
  links.forEach((link) => link.setAttribute('href', href));
}

/**
 * Apply the brand favicon, falling back to a generated initials icon when the
 * configured asset is missing or fails to load. Preloads the asset via an
 * `Image` so a broken path never leaves the default Tagada favicon in place.
 */
export function applyBrandFavicon(favicon: string | undefined, appName: string, color: string): void {
  if (typeof document === 'undefined') return;

  const fallback = () => {
    // Incomplete/neutral partner brand (no name): apply a blank favicon rather
    // than inventing an initials icon, replacing the static TagadaPay icons.
    if (!appName.trim()) {
      applyFavicon(BLANK_FAVICON);
      return;
    }
    const dataUrl = generateInitialsDataUrl(appName, color);
    if (dataUrl) applyFavicon(dataUrl);
  };

  if (!favicon) {
    fallback();
    return;
  }

  const probe = new Image();
  probe.onload = () => applyFavicon(favicon);
  probe.onerror = fallback;
  probe.src = favicon;
}
